import {makeObservable} from 'mobx';

import type {Nullable} from '../../../utils/utility-types';
import type {PaymentPlanModel} from '../common/PaymentPlan/PaymentPlan.model';
import type {DateTimeModel} from '../Date/DateTime.model';
import type {FileModel} from '../File/File.model';
import type {SubtenantModel} from '../Subtenant/Subtenant.model';
import type {UserModel} from '../User/User.model';

import {BaseModel} from '../../../core/domain/Base/Base.model';
import {ProgramDurationModel} from '../common/ProgramDuration/ProgramDuration.model';

const ModelName = 'OfferModel';

export class OfferModel extends BaseModel {
  avatar: Nullable<FileModel>;
  client: Nullable<UserModel>;
  clientId: Nullable<string>;
  clientName: Nullable<string>;
  clientStatus: OfferModel.ClientStatus;
  coachIds: string[];
  concierge: Nullable<UserModel>;
  continuation: Nullable<OfferModel.Continuation>;
  created: DateTimeModel;
  cycleDuration: number;
  description: Nullable<string>;
  employer: Nullable<UserModel>;
  isAutoRestarting: boolean;
  numberOfCycles: number;
  parentOfferId: Nullable<string>;
  paymentPlan: Nullable<PaymentPlanModel>;
  sessions: {
    count: {
      base: number;
      extra: number;
      total: number;
    };
    duration: number;
    intersessionBreak: number;
    minPause: number;
  };
  subtenants: Nullable<SubtenantModel>[];
  title: string;
  type: OfferModel.Type;
  updated: DateTimeModel;
  userQuestionnaireId: Nullable<string>;
  visibility: OfferModel.Visibility;

  constructor(props: OfferModel.Props) {
    super(props);

    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<OfferModel, OfferModel.Props>(props),
    );
  }

  get duration(): ProgramDurationModel {
    return new ProgramDurationModel(this.cycleDuration * this.numberOfCycles);
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }
}

export namespace OfferModel {
  export enum ClientStatus {
    ARCHIVED = 'archived',
    CANCELED = 'canceled',
    COACH_ASSIGNED = 'coach_assigned',
    COACHES_OFFERED = 'coaches_offered',
    CONCIERGE_ASSIGNED = 'concierge_assigned',
    ONGOING = 'ongoing',
    QUESTIONNAIRE_ANSWERED = 'questionnaire_answered',
    QUESTIONNAIRE_SENT = 'questionnaire_sent',
    REGISTERED = 'registered',
    WAITING_FOR_MATCHING = 'waiting_matching',
  }

  export enum Continuation {
    INITIAL = 'initial',
    RENEWAL = 'renewal',
  }

  export enum Type {
    CORPORATE = 'corporate',
    PERSONAL = 'personal',
  }

  export enum Visibility {
    ARCHIVED = 'archived',
    PRIVATE = 'private',
    PUBLIC = 'public',
  }

  export type Props = BaseModel.ModelToProps<OfferModel, 'duration'>;
}
